import { useEffect } from "react";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  CheckCircle2, 
  Activity, 
  FileText, 
  Stethoscope, 
  Brain, 
  Users, 
  Layout, 
  MessageSquare,
  ChevronRight,
  Database,
  Mic
} from "lucide-react";
import { Button, Tabs, TabsContent, TabsList, TabsTrigger, Badge } from "@/ui";
import { useLocation } from "wouter";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Waveform component
const Waveform = () => {
  return (
    <div className="flex items-center gap-1 h-4">
      {[1, 2, 3, 4, 5, 4, 3, 2].map((i, idx) => (
        <motion.div
          key={idx}
          className="w-1 rounded-full"
          style={{ backgroundColor: ACCENT_COLOR }}
          animate={{ height: [4, 12, 4] }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: idx * 0.1,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

export default function Clinical() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBookDemo = () => {
    window.scrollTo(0, 0);
    setLocation("/book-demo");
  };

  return (
    <div 
      className="min-h-screen font-sans selection:bg-primary/10 selection:text-primary relative"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.div variants={fadeInUp}>
                <Badge className="mb-6 border-none" style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}>
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
                    <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: PRIMARY_BLUE }}></span>
                  </span>
                  New: Ambient Clinical Intelligence
                </Badge>
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp} 
                className="text-4xl lg:text-5xl font-normal leading-[1.1] mb-6"
                style={{ color: PRIMARY_BLUE }}
              >
                Hands-Free Documentation That{' '}
                <span 
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${ACCENT_COLOR} 100%)`
                  }}
                >
                  Captures Every Detail
                </span>
              </motion.h1>
              
              <motion.p variants={fadeInUp} className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg">
                Spend more time with patients, less time with keyboards. Our ambient AI listens, documents, and codes—so you can focus on what matters.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                  style={{ 
                    backgroundColor: PRIMARY_BLUE,
                    color: "white",
                    boxShadow: "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                  onClick={handleBookDemo}
                >
                  See It In Action 
                  <ArrowRight className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
                </Button>
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto gap-2 font-bold rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                  style={{
                    backgroundColor: "#0d3b6e",
                    color: "white",
                    boxShadow: "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <FileText className="w-5 h-5" style={{ color: ACCENT_COLOR }} /> 
                  Download Brief
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-center gap-8 pt-4">
                <div className="flex flex-col">
                  <span className="text-3xl font-medium" style={{ color: PRIMARY_BLUE }}>70%</span>
                  <span className="text-sm text-gray-500 font-normal">Time Saved</span>
                </div>
                <div className="w-px h-10 bg-gray-300" />
                <div className="flex flex-col">
                  <span className="text-3xl font-medium" style={{ color: PRIMARY_BLUE }}>99%</span>
                  <span className="text-sm text-gray-500 font-normal">Accuracy</span>
                </div>
                <div className="w-px h-10 bg-gray-300" />
                <div className="flex flex-col">
                  <span className="text-3xl font-medium" style={{ color: PRIMARY_BLUE }}>0</span>
                  <span className="text-sm text-gray-500 font-normal">Clicks Needed</span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/50 bg-white">
                <img 
                  src="/assets/solutions/clinical/doctor_and_patient_consultation_with_subtle_digital_overlay.png" 
                  alt="Clinical Ambient Intelligence" 
                  className="w-full h-auto" 
                />
                
                {/* Floating UI Elements Overlays */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 0.5 }}
                  className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-4 rounded-xl shadow-lg border border-gray-100"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                        <Mic className="w-4 h-4 text-red-500" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Live Recording</span>
                    </div>
                    <Waveform />
                  </div>
                  <div className="space-y-2 text-sm bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <p><span className="font-medium" style={{ color: PRIMARY_BLUE }}>Dr. Smith:</span> Have you been experiencing any shortness of breath with that?</p>
                    <p><span className="font-medium text-gray-600">Patient:</span> Only when I walk up the stairs, otherwise it's fine.</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-20 bg-white/50 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <MessageSquare className="w-6 h-6 text-white" />,
                title: "Real-Time Transcription",
                desc: "Live voice capture with speaker diarization ensures you never miss patient context."
              },
              {
                icon: <FileText className="w-6 h-6 text-white" />,
                title: "AI-Generated SOAP Notes",
                desc: "Auto-structured HPI, ROS, Physical Exam, and Plan for consistent documentation quality."
              },
              {
                icon: <Database className="w-6 h-6 text-white" />,
                title: "Medical Code Suggestions",
                desc: "Intelligent ICD-10 and CPT code recommendations derived directly from the conversation."
              }
            ].map((feature, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group p-8 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-primary/20 transition-all duration-300 hover:-translate-y-1"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: ACCENT_COLOR }}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-normal mb-3" style={{ color: PRIMARY_BLUE }}>{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed font-normal">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Subtle white overlay to ensure readability over background if needed */}
        <div className="absolute inset-0 bg-white/60 -z-10" />
        
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>AI-Powered Patient Flow</h2>
            <p className="text-lg text-gray-600 font-normal">From check-in to discharge, our intelligent workflow adapts to every stage of the patient journey.</p>
          </div>

          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute top-1/2 left-0 w-full h-px bg-gray-200 -translate-y-1/2 hidden lg:block" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 relative z-10">
              {[
                { step: "Intake", icon: Users, ai: "AI Chat Bot" },
                { step: "Triage", icon: Activity, ai: "Acuity Scoring" },
                { step: "Exam", icon: Stethoscope, ai: "Ambient Scribe" },
                { step: "Assess", icon: Brain, ai: "Dx Suggest" },
                { step: "Orders", icon: FileText, ai: "Smart Sets" },
                { step: "Discharge", icon: CheckCircle2, ai: "Summary Gen" }
              ].map((item, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex flex-col items-center text-center"
                >
                  <div 
                    className="w-16 h-16 rounded-full bg-white border-2 shadow-lg flex items-center justify-center mb-4 relative z-10 group cursor-pointer transition-colors"
                    style={{ borderColor: "#e5e7eb" }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = PRIMARY_BLUE}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = "#e5e7eb"}
                  >
                    <item.icon 
                      className="w-6 h-6 text-gray-400 group-hover:text-primary transition-colors" 
                      style={{ color: "inherit" }}
                      onMouseEnter={(e) => e.currentTarget.style.color = PRIMARY_BLUE}
                      onMouseLeave={(e) => e.currentTarget.style.color = ""}
                    />
                    <div 
                      className="absolute -bottom-2 text-white text-[10px] px-2 py-0.5 rounded-full font-medium shadow-sm"
                      style={{ backgroundColor: PRIMARY_BLUE }}
                    >
                      AI
                    </div>
                  </div>
                  <h4 className="font-medium mb-1" style={{ color: PRIMARY_BLUE }}>{item.step}</h4>
                  <p className="text-sm font-medium" style={{ color: PRIMARY_BLUE }}>{item.ai}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-gray-200 pt-12">
            {[
              { label: "Door-to-Discharge", value: "-25%" },
              { label: "Clicks Per Visit", value: "-60%" },
              { label: "Doc Completeness", value: "98%" },
              { label: "Patient Throughput", value: "+35%" },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-medium mb-2" style={{ color: PRIMARY_BLUE }}>{stat.value}</div>
                <div className="text-sm text-gray-500 uppercase tracking-wider font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Copilot Section */}
      <section className="py-24 bg-white/70 relative">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
               <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="relative rounded-xl overflow-hidden shadow-2xl border border-white"
               >
                 <img 
                   src="/assets/solutions/clinical/modern_medical_software_interface_mockup.png" 
                   alt="AI Clinical Copilot Interface" 
                   className="w-full h-auto" 
                 />
                 
                 {/* Interactive hotspot markers */}
                 <motion.div 
                   initial={{ scale: 0 }}
                   whileInView={{ scale: 1 }}
                   transition={{ delay: 0.5 }}
                   className="absolute top-[20%] right-[25%] w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                   style={{ backgroundColor: `${PRIMARY_BLUE}30` }}
                   onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${PRIMARY_BLUE}50`}
                   onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${PRIMARY_BLUE}30`}
                 >
                   <div className="w-3 h-3 rounded-full animate-ping" style={{ backgroundColor: PRIMARY_BLUE }} />
                 </motion.div>
               </motion.div>
            </div>
            
            <div className="order-1 lg:order-2 space-y-8">
              <Badge 
                className="bg-white border px-4 py-1.5 text-sm font-medium"
                style={{ color: PRIMARY_BLUE, borderColor: `${PRIMARY_BLUE}20` }}
              >
                Clinical Copilot
              </Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal leading-tight" style={{ color: PRIMARY_BLUE }}>
                Your Intelligent Clinical Assistant, <span style={{ color: PRIMARY_BLUE }}>Always Context-Aware</span>
              </h2>
              <p className="text-lg text-gray-600 font-normal">
                The AI Copilot sits alongside your workflow, providing proactive recommendations, risk alerts, and one-click actions based on real-time patient data.
              </p>
              
              <div className="space-y-6">
                {[
                  { title: "Contextual Suggestions", desc: "Proactive recommendations for care gaps and risk alerts." },
                  { title: "Smart Task Queue", desc: "Load balance optimization and result flagging." },
                  { title: "Interactive Chatbot", desc: "Ask clinical questions and get evidence-based answers." }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div 
                      className="w-6 h-6 rounded-full bg-white border flex items-center justify-center shrink-0 mt-1"
                      style={{ borderColor: `${PRIMARY_BLUE}20` }}
                    >
                      <CheckCircle2 className="w-4 h-4" style={{ color: PRIMARY_BLUE }} />
                    </div>
                    <div>
                      <h4 className="font-medium" style={{ color: PRIMARY_BLUE }}>{item.title}</h4>
                      <p className="text-gray-600 font-normal">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-2">
                   {[1,2,3].map(i => (
                     <div key={i} className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white" />
                   ))}
                </div>
                <div className="text-sm font-medium">
                  <span className="block" style={{ color: PRIMARY_BLUE }}>Clinician Reviewed AI</span>
                  <span style={{ color: PRIMARY_BLUE }}>98% Alert Relevance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role Based Views Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>Purpose-Built for Every Role</h2>
            <p className="text-lg text-gray-600 font-normal">Tailored interfaces that adapt to the specific needs of physicians, nurses, and medical assistants.</p>
          </div>

          <Tabs defaultValue="physician" className="w-full max-w-5xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto p-2 bg-gray-100/80 rounded-full mb-12">
              <TabsTrigger value="physician" className="rounded-full py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm cursor-pointer font-medium text-gray-600" style={{ color: "inherit" }}>
                Physician
              </TabsTrigger>
              <TabsTrigger value="nurse" className="rounded-full py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm cursor-pointer font-medium text-gray-600">
                Nurse
              </TabsTrigger>
              <TabsTrigger value="ma" className="rounded-full py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm cursor-pointer font-medium text-gray-600">
                Medical Assistant
              </TabsTrigger>
              <TabsTrigger value="coordinator" className="rounded-full py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm cursor-pointer font-medium text-gray-600">
                Coordinator
              </TabsTrigger>
            </TabsList>
            
            <div className="bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden min-h-[400px]">
              <TabsContent value="physician" className="m-0">
                <div className="grid md:grid-cols-2 h-full">
                  <div className="p-10 flex flex-col justify-center space-y-6">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${PRIMARY_BLUE}10`, color: PRIMARY_BLUE }}
                    >
                      <Stethoscope className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-normal" style={{ color: PRIMARY_BLUE }}>Physician View</h3>
                    <p className="text-gray-600 font-normal">Focused on clinical decision making and rapid documentation. Includes full AI Copilot and diagnostic suggestions.</p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <ChevronRight className="w-4 h-4" style={{ color: PRIMARY_BLUE }} /> Encounter & Assessment
                      </li>
                      <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <ChevronRight className="w-4 h-4" style={{ color: PRIMARY_BLUE }} /> Ambient Documentation
                      </li>
                      <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <ChevronRight className="w-4 h-4" style={{ color: PRIMARY_BLUE }} /> E-Prescribing
                      </li>
                    </ul>
                  </div>
                  <div className="p-10 flex items-center justify-center" style={{ backgroundColor: `${PRIMARY_BLUE}05` }}>
                     <div className="w-full max-w-sm">
                       <img 
                          src="/assets/solutions/clinical/minimalist_3d_illustration_of_a_digital_medical_tablet_and_stethoscope.png" 
                          alt="Physician Dashboard Illustration" 
                          className="w-full h-auto drop-shadow-xl rounded-lg hover:scale-105 transition-transform duration-500"
                        />
                     </div>
                  </div>
                </div>
              </TabsContent>

               <TabsContent value="nurse" className="m-0">
                <div className="grid md:grid-cols-2 h-full">
                  <div className="p-10 flex flex-col justify-center space-y-6">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
                      <Activity className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-normal" style={{ color: PRIMARY_BLUE }}>Nurse View</h3>
                    <p className="text-gray-600 font-normal">Optimized for triage, vitals capture, and patient education. Features smart templates and acuity scoring.</p>
                    <ul className="space-y-3">
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-teal-500" /> Triage Workbench
                       </li>
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-teal-500" /> Vitals Trending
                       </li>
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-teal-500" /> Discharge Management
                       </li>
                    </ul>
                  </div>
                  <div className="bg-teal-50/50 p-10 flex items-center justify-center">
                     <div className="w-full max-w-sm">
                        <img 
                          src="/assets/solutions/clinical/minimalist_3d_illustration_of_a_heart_monitor_and_medical_cross.png" 
                          alt="Nurse Dashboard Illustration" 
                          className="w-full h-auto drop-shadow-xl rounded-lg hover:scale-105 transition-transform duration-500"
                        />
                     </div>
                  </div>
                </div>
              </TabsContent>

               <TabsContent value="ma" className="m-0">
                <div className="grid md:grid-cols-2 h-full">
                  <div className="p-10 flex flex-col justify-center space-y-6">
                    <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                      <Users className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-normal" style={{ color: PRIMARY_BLUE }}>MA View</h3>
                    <p className="text-gray-600 font-normal">Streamlines intake, rooming, and order execution. Automated task tracking helps keep the clinic running smoothly.</p>
                     <ul className="space-y-3">
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-purple-500" /> Patient Intake
                       </li>
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-purple-500" /> Room Status
                       </li>
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-purple-500" /> Order Tracking
                       </li>
                    </ul>
                  </div>
                  <div className="bg-purple-50/50 p-10 flex items-center justify-center">
                     <div className="w-full max-w-sm">
                        <img 
                          src="/assets/solutions/clinical/minimalist_3d_illustration_of_a_checklist_and_calendar.png" 
                          alt="Medical Assistant Dashboard Illustration" 
                          className="w-full h-auto drop-shadow-xl rounded-lg hover:scale-105 transition-transform duration-500"
                        />
                     </div>
                  </div>
                </div>
              </TabsContent>

               <TabsContent value="coordinator" className="m-0">
                <div className="grid md:grid-cols-2 h-full">
                  <div className="p-10 flex flex-col justify-center space-y-6">
                    <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
                      <Layout className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-normal" style={{ color: PRIMARY_BLUE }}>Coordinator View</h3>
                    <p className="text-gray-600 font-normal">Bird's eye view of patient flow and clinic resources. Manage load balancing and wait times effectively.</p>
                     <ul className="space-y-3">
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-orange-500" /> CareFlow Dashboard
                       </li>
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-orange-500" /> Provider Load Balancing
                       </li>
                       <li className="flex items-center gap-2 text-sm font-medium text-gray-700">
                         <ChevronRight className="w-4 h-4 text-orange-500" /> SLA Alerts
                       </li>
                    </ul>
                  </div>
                  <div className="bg-orange-50/50 p-10 flex items-center justify-center">
                     <div className="w-full max-w-sm">
                        <img 
                          src="/assets/solutions/clinical/minimalist_3d_illustration_of_connected_nodes_and_workflow.png" 
                          alt="Care Coordinator Dashboard Illustration" 
                          className="w-full h-auto drop-shadow-xl rounded-lg hover:scale-105 transition-transform duration-500"
                        />
                     </div>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ backgroundColor: PRIMARY_BLUE }}
        >
           {/* Abstract mesh gradient background */}
           <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br" style={{ background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #0f4b91 50%, #0a3568 100%)` }} />
           <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-3xl lg:text-4xl font-normal text-white mb-6"
          >
            Ready to Transform Your Clinical Workflow?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto font-normal"
          >
            Join forward-thinking healthcare providers who are saving hours every day with Aiscendus Clinical Intelligence.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              size="lg" 
              className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
              style={{
                backgroundColor: "white",
                color: PRIMARY_BLUE,
                boxShadow: "0 4px 0 rgba(255,255,255,0.7), 0 6px 20px rgba(255,255,255,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
              onClick={handleBookDemo}
            >
              Request a Demo
              <ArrowRight className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
            </Button>
            <Button 
              size="lg" 
              className="w-full sm:w-auto gap-2 font-bold rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
              style={{
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "white",
                boxShadow: "0 4px 0 rgba(255,255,255,0.3), 0 6px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.3)",
              }}
              onClick={() => setLocation("/contact")}
            >
              <MessageSquare className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
              Contact Sales
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
